/**
 * A screen's rows (section 7): the row types a brand can add, in plain words with the business's
 * nouns ("Today's class", "Coming up: services"), what each needs (a collection, a category), and
 * the list operations (move, hide, remove, add). The model's own names (rails, kinds, terms) never
 * reach the screen. Pure, unit-tested.
 */

import type { Pack } from "../vocabulary/vocabulary";

export type RailKind = "collection" | "collections" | "today" | "continue-watching" | "my-list" | "live-now" | "upcoming" | "recently-added" | "my-series" | "term" | "up-next" | "schedule";

export interface Rail {
  kind: RailKind;
  title?: Record<string, string>;
  collectionId?: string;
  collectionIds?: string[];
  termId?: string;
  limit?: number;
}

export interface RowType {
  kind: RailKind;
  label: string;
  description: string;
  /** What the row must be given before it can show anything. */
  needs: "collection" | "collections" | "term" | null;
  /** Takes "show at most". */
  limit: boolean;
  /** Built from each viewer's own data: the preview shows a sample. */
  personal: boolean;
}

/** What the brand has, which decides which row types make sense. */
export interface RowContext {
  live: boolean;
  series: boolean;
  programmes: boolean;
  stages: boolean;
  categories: boolean;
}

const low = (s: string) => s.toLowerCase();

export function rowTypes(pack: Pack, has: RowContext): RowType[] {
  const titles = low(pack.nouns.title.many);
  const title = low(pack.nouns.title.one);
  const series = low(pack.nouns.series.many);
  const live = low(pack.nouns.liveEvent.many);
  const all: Array<RowType & { when: boolean }> = [
    { kind: "collection", label: `A ${low(pack.nouns.collection.one)}`, description: `${titles[0]!.toUpperCase()}${titles.slice(1)} you choose, in your order: one of your ${low(pack.nouns.collection.many)}.`, needs: "collection", limit: false, personal: false, when: true },
    { kind: "collections", label: `A shelf of ${low(pack.nouns.collection.many)}`, description: `Several ${low(pack.nouns.collection.many)} as cards; each opens its own page.`, needs: "collections", limit: false, personal: false, when: true },
    { kind: "today", label: `Today's ${title}`, description: `The day each viewer has reached in the ${low(pack.nouns.series.one)} they follow ("Day 4 of 21").`, needs: null, limit: false, personal: true, when: has.programmes },
    { kind: "continue-watching", label: "Continue watching", description: `What each viewer started and hasn't finished.`, needs: null, limit: false, personal: true, when: true },
    { kind: "up-next", label: "Up next", description: `The next ${title} in each of the ${series} a viewer started.`, needs: null, limit: false, personal: true, when: has.series },
    { kind: "my-series", label: `My ${series}`, description: `The ${series} each viewer follows.`, needs: null, limit: true, personal: true, when: has.series },
    { kind: "my-list", label: "My list", description: "What each viewer saved for later.", needs: null, limit: false, personal: true, when: true },
    { kind: "recently-added", label: "Recently added", description: `Your newest ${titles}, newest first.`, needs: null, limit: true, personal: false, when: true },
    { kind: "term", label: `One ${low(pack.nouns.taxonomy.one)}`, description: `Everything in one of your ${low(pack.nouns.taxonomy.many)}, e.g. all comedies.`, needs: "term", limit: true, personal: false, when: has.categories },
    { kind: "live-now", label: "Live now", description: `${live[0]!.toUpperCase()}${live.slice(1)} happening right now. Hidden when nothing is live.`, needs: null, limit: false, personal: false, when: has.live },
    { kind: "upcoming", label: "Coming up", description: `Upcoming ${live}, soonest first.`, needs: null, limit: false, personal: false, when: has.live },
    { kind: "schedule", label: "Schedule", description: `${pack.nouns.stage.many} and their ${live} on a time grid.`, needs: null, limit: false, personal: false, when: has.stages },
  ];
  return all.filter((t) => t.when).map(({ when: _when, ...t }) => t);
}

/** The type of any row, even one this brand would not be offered now (it was added before). */
export function rowTypeOf(kind: string, pack: Pack): RowType {
  const every = rowTypes(pack, { live: true, series: true, programmes: true, stages: true, categories: true });
  return every.find((t) => t.kind === kind) ?? { kind: kind as RailKind, label: "A row the console does not know", description: "Kept as it is.", needs: null, limit: false, personal: false };
}

/**
 * How a row reads in the list: its title as viewers see it (its own title, the collection's name,
 * the category's name, or the type) and what kind of row it is.
 */
export function rowSummary(rail: Rail, pack: Pack, names: { collection: (id: string) => string | undefined; term: (id: string) => string | undefined }, locale: string): { title: string; type: string; missing?: string } {
  const type = rowTypeOf(rail.kind, pack);
  const own = rail.title?.[locale] || Object.values(rail.title ?? {})[0];
  switch (rail.kind) {
    case "collection": {
      const name = rail.collectionId ? names.collection(rail.collectionId) : undefined;
      return { title: own || name || "Choose a collection", type: type.label, ...(rail.collectionId && !name ? { missing: "That collection is not in your Library any more." } : !rail.collectionId ? { missing: "No collection chosen yet." } : {}) };
    }
    case "collections": {
      const ids = rail.collectionIds ?? [];
      const found = ids.map((id) => names.collection(id)).filter(Boolean);
      return { title: own || (found.length ? found.join(", ") : "Choose collections"), type: type.label, ...(!ids.length ? { missing: "No collections chosen yet." } : {}) };
    }
    case "term": {
      const name = rail.termId ? names.term(rail.termId) : undefined;
      return { title: own || name || `Choose a ${low(pack.nouns.taxonomy.one)}`, type: type.label, ...(!rail.termId ? { missing: `No ${low(pack.nouns.taxonomy.one)} chosen yet.` } : !name ? { missing: `That ${low(pack.nouns.taxonomy.one)} is not in your Library any more.` } : {}) };
    }
    default:
      return { title: own || type.label, type: own ? type.label : type.description.split(".")[0]! };
  }
}

/** A new row of a type: what it needs is chosen in its settings right after. */
export function newRail(kind: RailKind): Rail {
  switch (kind) {
    case "collections": return { kind, collectionIds: [] };
    default: return { kind };
  }
}

export function moveItem<T>(list: T[], from: number, to: number): T[] {
  if (from === to || to < 0 || to >= list.length || from < 0 || from >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item!);
  return next;
}

/** A row as the editor holds it: the rail and whether it is hidden (kept in the list, not in the app). */
export interface RowEntry { key: string; rail: Rail; hidden: boolean }

/** The rails the app gets: the shown rows, in order, without what the editor added to them. */
export function railsOf(rows: RowEntry[]): Rail[] {
  return rows.filter((r) => !r.hidden).map((r) => r.rail);
}

/**
 * The editor's rows from the saved rails and the hidden rows remembered for this screen (each
 * with the position it had), so a hidden row comes back where it was.
 */
export function rowsFrom(rails: Rail[], hidden: Array<{ at: number; rail: Rail }>): RowEntry[] {
  const rows: RowEntry[] = rails.map((rail, i) => ({ key: `r${i}`, rail, hidden: false }));
  [...hidden].sort((a, b) => a.at - b.at).forEach((h, i) => rows.splice(Math.min(h.at, rows.length), 0, { key: `h${i}`, rail: h.rail, hidden: true }));
  return rows;
}

/** The hidden rows with their positions, to remember. */
export function hiddenOf(rows: RowEntry[]): Array<{ at: number; rail: Rail }> {
  return rows.flatMap((r, at) => (r.hidden ? [{ at, rail: r.rail }] : []));
}
